import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Avatar, Button, Tooltip } from '@fluentui/react-components';
import {
  ArrowExit20Regular,
  Board20Regular,
  BuildingPeople20Regular,
  ChartMultiple20Regular,
  ClipboardTaskListLtr20Regular,
  DarkTheme20Regular,
  Filter20Regular,
  Folder20Regular,
  History20Regular,
  Money20Regular,
  Navigation20Regular,
  Person20Regular,
  ScaleFit20Regular,
  Tag20Regular,
  WeatherSunny20Regular,
} from '@fluentui/react-icons';
import { useAuth } from '../context/AuthContext';

interface NavigationProps {
  isMobileOpen: boolean;
  onToggle: () => void;
  onNavigate: () => void;
  isDarkMode: boolean;
  onToggleTheme: () => void;
}

interface NavigationItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

const Navigation: React.FC<NavigationProps> = ({
  isMobileOpen,
  onToggle,
  onNavigate,
  isDarkMode,
  onToggleTheme
}) => {
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return null;
  }

  const isActive = (path: string) => {
    return location.pathname === path || (path === '/' && location.pathname === '/dashboard');
  };

  const navigationItems: NavigationItem[] = [
    { path: '/', label: 'Dashboard', icon: <Board20Regular /> },
    { path: '/customers', label: 'Customers', icon: <BuildingPeople20Regular /> },
    { path: '/persons', label: 'Persons', icon: <Person20Regular /> },
    { path: '/transactions', label: 'Transactions', icon: <Money20Regular /> },
    { path: '/projects', label: 'Projects', icon: <Folder20Regular /> },
    { path: '/leads', label: 'Leads', icon: <Filter20Regular /> },
    { path: '/statistics', label: 'Statistics', icon: <ChartMultiple20Regular /> },
    { path: '/profitloss', label: 'P&L', icon: <ScaleFit20Regular /> },
    { path: '/projectcategories', label: 'Categories', icon: <Tag20Regular /> },
    { path: '/tags', label: 'Customer Tags', icon: <Tag20Regular /> },
    { path: '/projecttags', label: 'Project Tags', icon: <Tag20Regular /> },
    { path: '/activities', label: 'Activities', icon: <ClipboardTaskListLtr20Regular /> },
    { path: '/activity-log', label: 'Activity Log', icon: <History20Regular /> },
  ];

  return (
    <aside className={`sidebar m365-sidebar ${isMobileOpen ? 'sidebar-open' : ''}`} aria-label="Primary navigation">
      <div className="sidebar-mobile-header">
        <Button
          appearance="subtle"
          icon={<Navigation20Regular />}
          onClick={onToggle}
          aria-label="Close navigation"
        />
      </div>

      <div className="m365-sidebar-brand">
        <Link to="/" className="m365-brand-link" onClick={onNavigate}>
          <span className="m365-brand-mark" aria-hidden="true">F</span>
          <span>
            <span className="m365-brand-title">DevRain Flow</span>
            <span className="m365-brand-subtitle">Finance workspace</span>
          </span>
        </Link>
        <Tooltip content={isDarkMode ? 'Switch to light theme' : 'Switch to dark theme'} relationship="label">
          <Button
            appearance="subtle"
            shape="circular"
            icon={isDarkMode ? <WeatherSunny20Regular /> : <DarkTheme20Regular />}
            onClick={onToggleTheme}
            aria-label={isDarkMode ? 'Switch to light theme' : 'Switch to dark theme'}
          />
        </Tooltip>
      </div>

      <nav className="m365-nav" aria-label="Flow sections">
        <div className="m365-nav-section-label">Workspace</div>
        <ul className="m365-nav-list">
          {navigationItems.map((item) => {
            const active = isActive(item.path);

            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`nav-link m365-nav-link ${active ? 'active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  onClick={onNavigate}
                >
                  <span className="m365-nav-icon" aria-hidden="true">{item.icon}</span>
                  <span className="m365-nav-label">{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="m365-user-card">
        <div className="m365-user-summary">
          <Avatar name={user?.email || 'User'} color="brand" />
          <div className="m365-user-text">
            <div className="m365-user-email">{user?.email || 'User'}</div>
            <div className="m365-user-role">Admin</div>
          </div>
        </div>

        <Button
          appearance="secondary"
          icon={<ArrowExit20Regular />}
          onClick={() => {
            logout();
            onNavigate();
          }}
          className="m365-logout-button"
        >
          Logout
        </Button>
      </div>
    </aside>
  );
};

export default Navigation;
